import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { StockInventory } from '../../../shared/models/stockInventory';
import { NoDataComponent } from '../../../shared/components/no-data/no-data.component';
import { ContextService } from '../../../shared/services/context.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StockService } from '../../../shared/services/stock.service';
import { MatSort } from '@angular/material/sort';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { Alert, AlertModule, AlertService, AlertType } from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NoScrollDirective } from '../../../directives/nx-no-scroll.directive';
import { IModif } from '../../../shared/models/IModif';
import { NavigationConfirmService } from '../../../shared/services/navigation-confirm.service';
import { YearScrollerComponent } from '../../fleet/year-scroller/year-scroller.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {COL_ACTION, COL_SEAT, COL_TIRE_SPEC, MAX_VALUE} from '../../../shared/constants/StockConstants';
import moment from 'moment/moment';
import {StockArrival} from "../../../shared/models/stockArrival";

@Component({
  selector: 'mic-stock-inventory-table',
  imports: [
    NoDataComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCell,
    TranslatePipe,
    MatCell,
    MatIcon,
    MatIconButton,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    AlertModule,
    NoScrollDirective,
    YearScrollerComponent,
    NgxMaskDirective
  ],
  templateUrl: './inventory-table.component.html',
  styleUrl: './inventory-table.component.scss',
  providers: [provideNgxMask()]
})
export class InventoryTableComponent implements OnInit, AfterViewInit, OnChanges, IModif, OnDestroy {
  siteId: number;
  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };

  /**
   * Table config properties
   */
  currentYear: number = new Date().getFullYear();
  currentMonth = new Date().getMonth() + 1;
  monthsNumbersToDisplay: number[] = Array.from(
    { length: 12 },
    (_, i) =>
      moment()
        .subtract(11 - i, 'months')
        .month() + 1
  );
  @ViewChild(MatSort) sort!: MatSort;
  dataSource = new MatTableDataSource<StockInventory>();
  columns = [COL_SEAT, COL_TIRE_SPEC, ...this.monthsNumbersToDisplay.map((m) => m.toString()), COL_ACTION];

  /**
   * Properties to manage component state and interactions with parent component
   */
  isLoadingData = model<boolean>();
  isSavingData = model<boolean>();
  isModificationsPending = model<boolean>(false);
  startYear = model<number>(new Date().getFullYear());
  startMonth = model<number>(new Date().getMonth() + 1);
  @Input() tireSpecFilteredId: number;
  @Input() manufacturerFiltered: string[] = [];
  @Output() dataLoaded = new EventEmitter<StockInventory[]>();

  /**
   * Form array to manage input controls for stock quantities
   */
  formArray: FormArray;
  quantitiesControls: FormGroup[] = [];

  constructor(
    private readonly stockService: StockService,
    private contextService: ContextService,
    private destroyRef: DestroyRef,
    private fb: FormBuilder,
    private alertService: AlertService,
    private translate: TranslateService,
    private navigationConfirm: NavigationConfirmService
  ) {
    this.contextService.register(this);
  }

  ngOnInit() {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadStockData();
      }
    });
    this.dataSource.sortingDataAccessor = (data, col) => {
      if (col === COL_SEAT) {
        return data.tireSpec.seat;
      } else if (col === COL_TIRE_SPEC) {
        return data.tireSpec.specification;
      }
    };
  }

  ngAfterViewInit() {
    // Filter predicate to handle both manufacturer filtering and tire specification filtering. If the filter is empty, all data is shown.
    this.dataSource.filterPredicate = (data: StockInventory, filter: string) => {
      if (!filter) return true;
      try {
        const manufacturers: string[] = JSON.parse(filter);
        return manufacturers.length === 0 || manufacturers.includes(data.tireSpec.manufacturer);
      } catch {
        // Fallback to tire spec filtering if filter is not a JSON array (manufacturer filter)
        return data.tireSpec.specification.toLowerCase().includes(filter.toLowerCase());
      }
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tireSpecFilteredId) {
      let tireSpecFiltered = this.dataSource.data
        .map((data) => data.tireSpec)
        .filter((tireSpec) => tireSpec.tireSpecId === this.tireSpecFilteredId)
        .map((tireSpec) => tireSpec.specification)[0];
      this.dataSource.filter = tireSpecFiltered?.trim().toLowerCase();
    }
    // If tireSpec filter also changed, manufacturer filter will be applied through the combinedLatest in parent component
    if (this.tireSpecFilteredId === undefined && changes.manufacturerFiltered) {
      this.applyManufacturerFilter();
    }
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

  getModifStatus(): boolean {
    return this.isModificationsPending();
  }

  /**
   * Load stock inventory data based on current search parameters (siteId, startYear, startMonth)
   * @private
   */
  private loadStockData() {
    if (!this.startYear() || !this.startMonth() || !this.siteId) return; // Ensure search params are defined, otherwise skip loading
    this.isLoadingData.set(true);
    this.dataSource.data = [] as StockInventory[];
    this.stockService
      .getStockInventories(this.siteId, this.startYear(), this.startMonth())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stocks: StockInventory[]) => {
        this.dataSource.data = stocks;
        this.dataLoaded.emit(stocks);
        this.isLoadingData.set(false);
        // Reset modifications pending flag, as we just loaded fresh data
        this.isModificationsPending.update(() => false);
        this.dataSource.sort = this.sort;
        this.buildForm(); // (Re)build the form with the new data
      });
  }

  /**
   * Apply manufacturer filter to the data source. If no manufacturers are selected, clear the filter to show all data.
   * @private
   */
  private applyManufacturerFilter() {
    if (!this.manufacturerFiltered || this.manufacturerFiltered.length === 0) {
      this.dataSource.filter = ''; // No filter applied
    } else {
      // Encoding filter for filterPredicate
      this.dataSource.filter = JSON.stringify(this.manufacturerFiltered);
    }
  }

  /**
   * Get the FormControl for a specific cell based on row index and tire specification ID. This allows us to bind the input fields in the template to the correct controls in the form array.
   * @param tireSpecId ID of the tire specification for the column
   * @returns FormControl for the specified cell, or null if not found
   * @protected
   */
  protected getQuantityControl(tireSpecId: number): FormControl | null {
    const group = this.quantitiesControls?.find((fg) => fg.contains(tireSpecId.toString()));
    return group ? (group.get(tireSpecId.toString()) as FormControl) : null;
  }

  /**
   * Build the form array based on the current data source. Each row corresponds to a tire specification, and each month column corresponds to a FormControl for the stock quantity. The form is rebuilt every time new data is loaded to ensure it reflects the latest state of the inventory.
   * @private
   */
  private buildForm(): void {
    const month = this.currentMonth;
    const firstDay = this.getFirstDayOfMonth(month);

    this.formArray = this.fb.array(
      this.dataSource.data.map((element: StockInventory) => {
        let stockData = element.stocksByMonth[firstDay];
        const value = stockData?.corrected ? stockData?.stockCorrection : stockData?.tireStockQty ?? null;
        const group = this.fb.group({
          [element.tireSpec.tireSpecId.toString()]: new FormControl(value, [Validators.min(0), Validators.max(MAX_VALUE)])
        });

        group.valueChanges.subscribe((val) => {
          const raw = val[element.tireSpec.tireSpecId.toString()];
          const newValue = Number(raw);
          const tireStockQty = element.stocksByMonth[firstDay]?.tireStockQty;

          if (raw === null || raw === undefined || isNaN(newValue)) {
            return;
          }

          element.stocksByMonth[firstDay].stockCorrection = newValue;
          element.stocksByMonth[firstDay].corrected = true;
          element.stocksByMonth[firstDay].modified = true;

          if (!element.stocksByMonth[firstDay] || newValue == tireStockQty) {
            element.stocksByMonth[firstDay] = {tireStockQty: tireStockQty, corrected: false, modified: false, stockCorrection: newValue};
          }

          this.isModificationsPending.set(true);
        });

        return group;
      })
    ) as FormArray<FormGroup>;

    this.quantitiesControls = this.formArray.controls as FormGroup[];
  }

  protected updateColumns(element: StockInventory, monthNumber: number, control: FormControl | null): void {
    const key = this.getFirstDayOfMonth(monthNumber);
    if (!element.stocksByMonth) {
      element.stocksByMonth = {};
    }

    let newQuantity = Number(control.value) || 0;
    if (!control.value) {
      control.setValue(0, { emitEvent: false });
      newQuantity = 0;
    } else if (newQuantity > MAX_VALUE) {
      newQuantity = MAX_VALUE;
      control.setValue(MAX_VALUE, { emitEvent: false });
    }

    const existing = element.stocksByMonth[key] as Partial<any> | undefined;
    const existingQuantity = Number(existing?.quantity) || 0;

    const modified = newQuantity !== existingQuantity;

    element.stocksByMonth[key] = {
      ...(existing || {}),
      quantity: newQuantity,
      modified: modified ? true : existing?.modified ?? false,
      deliveryDate: existing?.deliveryDate ?? key
    } as any;



    if (modified) {
      this.isModificationsPending.set(true);
    }
  }

  /**
   * Compute the year for a given month number based on the current year and displayed months
   * @param month Month number (1-12)
   * @returns Corresponding year
   */
  getYearForMonth(month: number) {
    let index = this.monthsNumbersToDisplay.findIndex((m) => m === month);
    let year = this.startYear();
    for (let i = 0; i < index; i++) {
      // If bigger month number found before, it means we passed to next year
      if (this.monthsNumbersToDisplay[i] > month) {
        return year + 1;
      }
    }
    return year;
  }

  /**
   * Get CSS class for a month cell based on whether it's editable
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns CSS class name or undefined
   */
  getMonthClass(month: number, year: number): string {
    if (!this.isEditableMonth(month, year)) return 'grey-cell';
  }

  /**
   * Determine if a month is editable based on current date
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns True if editable, false otherwise
   */
  isEditableMonth(month: number, year: number): boolean {
    return year === this.currentYear && month === this.currentMonth;
  }

  /**
   * Get the first day of a month in YYYY-MM-DD format
   * @param monthNumber Month number (1-12)
   * @returns String in format 'YYYY-MM-01'
   */
  getFirstDayOfMonth(monthNumber: number) {
    return moment({ year: this.getYearForMonth(monthNumber), month: monthNumber - 1, day: 1 }).format('YYYY-MM-DD');
  }

  /**
   * Methods for reverting modifications globally or on a specific line.
   */
  resetModifications() {
    this.loadStockData();
  }

  /**
   * Revert modifications for a specific tire
   * @param element StockInventory element corresponding to the line to revert
   */
  protected revertLineModifications(element: StockInventory) {
    this.navigationConfirm.confirmModal('common.reset-modal.title', 'common.reset-modal.description', () => {
      this.isLoadingData.set(true);
      let key = this.getFirstDayOfMonth(this.currentMonth);
      element.stocksByMonth[key].stockCorrection = element.stocksByMonth[key].tireStockQty;
      element.stocksByMonth[key].corrected = false;
      element.stocksByMonth[key].modified = false;
      this.buildForm(); // Rebuild the form with the updated data
      this.isModificationsPending.set(true);
      this.isLoadingData.set(false);
    });
  }

  protected isCurrentMonthCorrected(element: StockInventory): boolean {
    const key = this.getFirstDayOfMonth(this.currentMonth);
    return !element.stocksByMonth[key]?.corrected;
  }

  saveModifications() {
    this.isSavingData.set(true);
    // Prepare properties used for the save request
    const currentMonthYear = this.getFirstDayOfMonth(this.currentMonth);

    this.stockService
      .saveStockInventories(this.siteId, this.dataSource.data, currentMonthYear)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isModificationsPending.set(false);
          this.loadStockData(); // Reload data after saving to get updated values from backend and reset form state
          this.isSavingData.set(false);
          this.alertService.showAlert({
            type: AlertType.success,
            title: `${this.translate.instant('stock.table.inventoryTable.save.successTitle')}`
          } as Alert);
        },
        error: (err) => {
          let errorMsg: string | undefined = undefined;
          if (err.error && err.error.message) {
            errorMsg = `(${err.error.message})`;
          }
          this.isSavingData.set(false);
          this.alertService.showAlert({
            type: AlertType.critical,
            title: `${this.translate.instant('stock.table.inventoryTable.save.errorTitle')}`,
            content: `${this.translate.instant('stock.table.inventoryTable.save.errorMsg')} ${errorMsg}`
          } as Alert);
        }
      });
  }

  /**
   * Calculate the total stock quantity for a given month
   * @param monthNumber Month number (1-12)
   * @returns Total stock quantity for that month
   */
  getTotalForMonth(monthNumber: number): number {
    const key = this.getFirstDayOfMonth(monthNumber);
    return this.dataSource.data.reduce((sum, element) => {
      const stock = element.stocksByMonth[key];
      if (!stock) return 0; // If no stock data for this month, skip
      if (stock.corrected) {
        return sum + (Number(stock.stockCorrection) || 0);
      }
      return sum + (Number(stock.tireStockQty) || 0);
    }, 0);
  }

  /**
   * Method to determine if user can select next year in year scroller, based on selected start year and current year
   */
  protected canSelectNextYear() {
    return this.startYear() + 1 < new Date().getFullYear();
  }
}
